import { AuthService } from './auth'

// CRM API base URL
const CRM_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://api.quali-d.com'
  : 'https://api.quali-d.com'

export class CRMService {
  static async makeAuthenticatedRequest (endpoint, options = {}) {
    const token = AuthService.getAuthToken()

    if (!token) {
      throw new Error('No authentication token found')
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers
    }

    const response = await fetch(`${CRM_API_BASE_URL}${endpoint}`, {
      ...options,
      headers
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }

    return response.json()
  }

  // Check CRM integration status
  static async getStatus () {
    try {
      return await this.makeAuthenticatedRequest('/crm/zoho/status')
    } catch (error) {
      console.error('Error fetching CRM status:', error)
      return {
        connected: false,
        error: error.message
      }
    }
  }

  // Initiate CRM connection
  static async connect () {
    try {
      return await this.makeAuthenticatedRequest('/crm/zoho/connect')
    } catch (error) {
      console.error('Error connecting to CRM:', error)
      throw error
    }
  }

  // Disconnect CRM integration
  static async disconnect () {
    try {
      return await this.makeAuthenticatedRequest('/crm/zoho/disconnect', {
        method: 'POST'
      })
    } catch (error) {
      console.error('Error disconnecting CRM:', error)
      throw error
    }
  }

  // Update sync settings
  static async updateSettings (settings) {
    try {
      return await this.makeAuthenticatedRequest('/crm/zoho/settings', {
        method: 'PUT',
        body: JSON.stringify(settings)
      })
    } catch (error) {
      console.error('Error updating CRM settings:', error)
      throw error
    }
  }

  // Get sync history
  static async getSyncHistory (params = {}) {
    try {
      const queryParams = new URLSearchParams()

      if (params.page) queryParams.append('page', params.page)
      if (params.per_page) queryParams.append('per_page', params.per_page)
      if (params.sync_type) queryParams.append('sync_type', params.sync_type)
      if (params.success !== undefined) queryParams.append('success', params.success)

      const query = queryParams.toString()
      const endpoint = query ? `/crm/zoho/sync-logs?${query}` : '/crm/zoho/sync-logs'

      return await this.makeAuthenticatedRequest(endpoint)
    } catch (error) {
      console.error('Error fetching sync history:', error)
      throw error
    }
  }

  // Manual sync call analysis
  static async syncCallAnalysis (analysisData) {
    try {
      return await this.makeAuthenticatedRequest('/crm/zoho/sync/call-analysis', {
        method: 'POST',
        body: JSON.stringify(analysisData)
      })
    } catch (error) {
      console.error('Error syncing call analysis:', error)
      throw error
    }
  }

  // Manual sync WhatsApp lead
  static async syncWhatsAppLead (leadData) {
    try {
      return await this.makeAuthenticatedRequest('/crm/zoho/sync/whatsapp-lead', {
        method: 'POST',
        body: JSON.stringify(leadData)
      })
    } catch (error) {
      console.error('Error syncing WhatsApp lead:', error)
      throw error
    }
  }

  // Helper method to format sync status for display
  static formatSyncStatus (status) {
    if (!status) return { text: 'Unknown', color: 'gray', icon: '?' }

    if (status.connected) {
      if (status.token_expired) {
        return { text: 'Token Expired', color: 'red', icon: '⚠️' }
      }
      return { text: 'Connected', color: 'green', icon: '✓' }
    }

    return { text: 'Disconnected', color: 'gray', icon: '○' }
  }

  // Helper method to format last sync time
  static formatLastSync (timestamp) {
    if (!timestamp) return 'Never'

    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString()
  }
}
